import dayjs from "dayjs";
import {
  preparePhoneForFormValues,
  preparePhoneForPost,
} from "src/shared/helpers/phone.helpers";
import { IAddressShort } from "src/shared/types/address.types";
import { EAgencies, ECountries } from "src/shared/types/common.types";
import {
  EMEssengers,
  IPartnerContactDTO,
  IPartnerDto,
} from "src/shared/types/partner.types";
import { IBankAccountFormValues } from "src/shared/types/partnerBankAccount";
import { licenseDefaultValue } from "../constants";
import {
  IPartnerBaseFormValues,
  IPartnerContactFormValues,
  IPartnerFormValues,
  IPostPartnerContactDTO,
  IPostPartnerDTO,
  IPutPartnerDTO,
} from "../types";
import { IEditPartnerFormValues } from "../types/partner";

export const getPartnerFormValues = (
  partner: IPartnerDto,
): IEditPartnerFormValues => {
  const license = partner.license
    ? {
        id: partner.license.id,
        number: partner.license.number,
        issued_date: dayjs(partner.license.issued_date),
        issued_by: partner.license.issued_by,
      }
    : undefined;

  return {
    partner: {
      id: partner.id,
      agency: partner.agency.value,
      registration_country_id: partner.registration_country.id,
      main_manager_id: partner.manager_main.id,
      organization_form: partner.organization_form.value,
      ownership_id: partner.ownership.id,
      name: partner.name,
      nonresident_phone: preparePhoneForFormValues(partner.nonresident_phone),
      nonresident_email: partner.nonresident_email || "",
      status: partner.status.value,
      priority: partner.priority.value,
      phone: preparePhoneForFormValues(partner.phone),
      fax: preparePhoneForFormValues(partner.fax),
      region: partner.region,
      activity_spheres: partner.activity_spheres?.map((sphere) => sphere.id),
      easy_service: partner.easy_service.value,
      easy_service_phone: partner.easy_service_phone.value,
      carrot_quest: partner.carrot_quest.value,
      is_EDMS_enabled: partner.is_EDMS_enabled,
      edin_email: partner.edin_email?.map((email) => ({ email })) || [],
      is_resident: partner.is_resident,
      is_nda_signed: partner.is_nda_signed,
      nda_note: partner.nda_note || "",
      is_survey_available: partner.is_survey_available,
      registration_number: partner.registration_number || "",
      okpo: partner.okpo || "",
      kpp: partner.kpp || "",
      ogrn: partner.ogrn || "",
      is_in_blacklist: partner.is_in_blacklist,
      nda_file: partner.nda_file,
      state_registration_certificate: partner.state_registration_certificate,
    },
    bank_account: {
      id: partner.bank_account?.id || "",
      bank_id: partner.bank_account?.bank.id || "",
      correspondent_bank_id: partner.bank_account?.correspondent_bank?.id || "",
      current_account: partner.bank_account?.current_account || "",
      transit_account: partner.bank_account?.transit_account || "",
      correspondent_account: partner.bank_account?.correspondent_account || "",
      bank_address: partner.bank_account?.bank.address
        ? {
            name: partner.bank_account.bank.address,
            value: partner.bank_account.bank.address,
          }
        : null,
      bank_name: partner.bank_account?.bank.name
        ? {
            value: partner.bank_account.bank.name,
            name: partner.bank_account.bank.name,
          }
        : null,
      bank_bic: partner.bank_account?.bank.bic
        ? {
            value: partner.bank_account.bank.bic,
            name: partner.bank_account.bank.bic,
          }
        : null,
      correspondent_bank_name: partner.bank_account?.correspondent_bank?.name
        ? {
            value: partner.bank_account.correspondent_bank.name,
            name: partner.bank_account.correspondent_bank.name,
          }
        : null,
      correspondent_bank_bic: partner.bank_account?.correspondent_bank?.bic
        ? {
            value: partner.bank_account.correspondent_bank.bic,
            name: partner.bank_account.correspondent_bank.bic,
          }
        : null,
    },
    license: license || licenseDefaultValue,
  };
};

export const getPartnerContactFullFormValues = ({
  firstname,
  lastname,
  loyalty_level,
  email,
  personality_type,
  phone_main,
  is_decision_maker,
  feedback_call_consent,
  technical_knowledge_level,
  phone_additional,
  emotional_level,
  position,
  surname,
  birthday,
  sex,
  messengers,
  access_to_pcp_enabled,
  access_to_pcp_finances_enabled,
  disable_pcp_access_reason,
  ...rest
}: IPartnerContactDTO): IPartnerContactFormValues => ({
  ...rest,
  birthday: dayjs(birthday),
  position: position?.value || "",
  surname: surname || "",
  firstname: firstname || "",
  lastname: lastname || "",
  sex: sex.value,
  email: email || "",
  phone_main: preparePhoneForFormValues(phone_main),
  phone_additional: preparePhoneForFormValues(phone_additional),
  feedback_call_consent: feedback_call_consent,
  is_decision_maker: is_decision_maker,
  technical_knowledge_level: technical_knowledge_level?.value || "",
  loyalty_level: loyalty_level?.value || "",
  personality_type: personality_type?.value || "",
  emotional_level: emotional_level?.value || "",
  access_to_pcp_enabled: access_to_pcp_enabled || false,
  access_to_pcp_finances_enabled: access_to_pcp_finances_enabled || false,
  disable_pcp_access_reason: disable_pcp_access_reason || "",
  messengers:
    messengers && messengers.length > 0
      ? messengers
      : [{ messenger: EMEssengers.SKYPE, login: "" }],
});

export function getPostPartnerContactDTO<T = IPostPartnerContactDTO>(
  contact: IPartnerContactFormValues,
): T {
  const {
    lastname,
    birthday,
    phone_additional,
    phone_main,
    note,
    messengers,
    ...rest
  } = contact;

  return {
    ...rest,
    lastname: lastname || null,
    birthday: birthday && birthday.isValid() ? birthday.format() : null,
    phone_main: preparePhoneForPost(phone_main),
    phone_additional: preparePhoneForPost(phone_additional),
    note: note || null,
    messengers: messengers?.[0].login ? messengers : null,
  } as T;
}

export const getPutPartnerDTO = (
  partner: IPartnerBaseFormValues,
): IPutPartnerDTO => {
  const partnerMapped = { ...partner };
  for (const key in partnerMapped) {
    if (partnerMapped[key as keyof IPartnerBaseFormValues] === "") {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      partnerMapped[key as keyof IPartnerBaseFormValues] = null;
    }
  }

  const {
    edin_email,
    phone,
    nonresident_phone,
    fax,
    ownership_id,
    region,
    ogrn,
    okpo,
    kpp,
    ...restPartner
  } = partnerMapped;

  const partnerDTO = {
    fax: fax ? preparePhoneForPost(fax) : null,
    phone: phone ? preparePhoneForPost(phone) : null,
    nonresident_phone: nonresident_phone
      ? preparePhoneForPost(nonresident_phone)
      : null,
    ownership_id: ownership_id || null,
    edin_email:
      restPartner.is_EDMS_enabled && edin_email?.length
        ? edin_email.map(({ email }) => email)
        : [],
    region: [
      EAgencies.Webcom_BY,
      EAgencies.IContext,
      EAgencies.Webcom_RU,
    ].includes(partner.agency as EAgencies)
      ? region
      : null,
    ogrn: [ECountries.RU].includes(
      partner.registration_country_id as ECountries,
    )
      ? ogrn
      : null,
    okpo: [ECountries.RU, ECountries.BY].includes(
      partnerMapped.registration_country_id as ECountries,
    )
      ? okpo
      : null,
    kpp: [ECountries.RU].includes(
      partnerMapped.registration_country_id as ECountries,
    )
      ? kpp
      : null,
    ...restPartner,
  };

  return {
    partner: partnerDTO,
  };
};

export const getPostPartnerDTO = (
  values: IPartnerFormValues,
): IPostPartnerDTO => {
  const { partner, license, addresses, contact_persons, bank_account } = values;

  const addressesForPost = [...addresses].filter(
    ({ country_id }) => !!country_id,
  );

  if (values.isPostAddressTheSame && addressesForPost.length > 0) {
    const legalAddress = values.addresses.find(
      (address) => address.type === "legal",
    );
    if (legalAddress) {
      addressesForPost.push({ ...legalAddress, type: "postal" });
    }
  }

  const hasLicenseValues =
    license && Object.values(license).some((val) => !!val);

  const partnerMapped = { ...partner };
  for (const key in partnerMapped) {
    if (partnerMapped[key as keyof IPartnerBaseFormValues] === "") {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      partnerMapped[key as keyof IPartnerBaseFormValues] = null;
    }
  }

  const {
    edin_email,
    phone,
    nonresident_phone,
    fax,
    ownership_id,
    region,
    ogrn,
    okpo,
    kpp,
    ...restPartner
  } = partnerMapped;

  return {
    partner: {
      fax: fax ? preparePhoneForPost(fax) : null,
      phone: phone ? preparePhoneForPost(phone) : null,
      nonresident_phone: nonresident_phone
        ? preparePhoneForPost(nonresident_phone)
        : null,
      ownership_id: ownership_id || null,
      edin_email:
        restPartner.is_EDMS_enabled && edin_email?.length
          ? edin_email.map(({ email }) => email)
          : [],
      region: [
        EAgencies.Webcom_BY,
        EAgencies.IContext,
        EAgencies.Webcom_RU,
      ].includes(partner.agency as EAgencies)
        ? region
        : null,
      ogrn:
        partnerMapped.registration_country_id === ECountries.RU ? ogrn : null,
      okpo: [ECountries.RU, ECountries.BY].includes(
        partnerMapped.registration_country_id as ECountries,
      )
        ? okpo
        : null,
      kpp: [ECountries.RU].includes(
        partnerMapped.registration_country_id as ECountries,
      )
        ? kpp
        : null,
      ...restPartner,
    },
    license: hasLicenseValues
      ? {
          ...license,
          issued_date: license.issued_date?.format() || null,
          issued_by: license.issued_by || null,
        }
      : null,

    contact_persons: contact_persons.map(
      ({ messengers, phone_main, phone_additional, ...rest }) => {
        const contactDto = {
          ...rest,
          messengers: messengers?.[0].login ? messengers : null,
          phone_main: preparePhoneForPost(phone_main),
          phone_additional: preparePhoneForPost(phone_additional),
          note: null,
          is_enabled: true,
          email_marketing_consent: false,
        };

        return contactDto;
      },
    ),
    addresses: addressesForPost.length ? addressesForPost : null,
    bank_account: {
      bank_id: bank_account.bank_id || null,
      current_account: bank_account.current_account || null,
      transit_account: bank_account.transit_account || null,
      correspondent_account: bank_account.correspondent_account || null,
      correspondent_bank_id: bank_account.correspondent_bank_id || null,
    },
  };
};

export const getPostAddressDto = (address: IAddressShort): IAddressShort => {
  return {
    ...address,
    postcode: address.postcode || null,
    region: address.region || null,
    area: address.area || null,
  };
};

export function getPostBankAccountDto(bank: IBankAccountFormValues) {
  const {
    bank_id,
    correspondent_bank_id,
    current_account,
    transit_account,
    correspondent_account,
  } = bank;

  return {
    bank_id: Number(bank_id),
    current_account,
    transit_account,
    correspondent_account,
    correspondent_bank_id: correspondent_bank_id
      ? Number(correspondent_bank_id)
      : null,
  };
}
